<template>
  <v-dialog :value="value" max-width="1100" scrollable @input="$emit('input', $event)">
    <v-card v-if="payload" class="dmd-card">
      <div class="d-flex justify-space-between align-start px-6 pt-6 pb-4 dmd-header">
        <div class="pr-4">
          <div class="dmd-overline mb-1">Dimitra {{ getDisplayModuleName(payload.moduleName) }}</div>
          <div class="dmd-title">{{ payload.title || $t('esgPlatform.assessmentFarmer.farmDetails') || 'Details' }}</div>
        </div>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="px-6 py-5">
        <div :class="['Soil','Harvesting'].includes(payload.moduleName) ? '' : 'dmd-layout'">
          <div class="dmd-main">
            <div class="dmd-grid">
              <div
                v-for="field in mainFields"
                :key="`field-${field.key}`"
                class="dmd-grid-item"
              >
                <div class="dmd-label">{{ getTranslatedFieldLabel(field) }}</div>
                <div class="dmd-value">
                  <template
                    v-if="isFarmModule(payload.moduleName) && String(field.key || '').toLowerCase() === 'location' && getLocationCoordinate(field.rawValue)"
                  >
                    <div class="d-flex align-center flex-wrap" style="gap: 12px;">
                      <span>{{ $t('esgPlatform.assessmentFarmer.point') || 'Point' }}</span>
                      <button type="button" class="dmd-link-btn" @click="openLocation(payload.title, field.rawValue)">
                        {{ $t('esgPlatform.assessmentFarmer.viewOnMap') || 'View on Map' }}
                      </button>
                    </div>
                  </template>
                  <template
                    v-else-if="isFarmModule(payload.moduleName) && String(field.key || '').toLowerCase() === 'farmgeofence' && getGeofenceCoordinates({ farmGeofence: field.rawValue }).length >= 3"
                  >
                    <div class="d-flex align-center flex-wrap" style="gap: 12px;">
                      <span>{{ $t('esgPlatform.assessmentFarmer.polygon') || 'Polygon' }}</span>
                      <button type="button" class="dmd-link-btn" @click="openGeofence(payload.title, field.rawValue)">
                        {{ $t('esgPlatform.assessmentFarmer.viewOnMap') || 'View on Map' }}
                      </button>
                    </div>
                  </template>
                  <template v-else>{{ field.value }}</template>
                </div>
              </div>
            </div>
          </div>

          <div v-if="payload.moduleName === 'Farms'" class="dmd-sidebar">
            <div v-if="areaField" class="dmd-metric-card mb-6">
              <div class="dmd-metric-label mb-2">{{ $t('esgPlatform.assessmentFarmer.totalArea') || 'Total Area' }}</div>
              <div class="d-flex align-end" style="gap: 6px;">
                <div class="dmd-metric-value">{{ areaValue }}</div>
                <div class="dmd-metric-unit">{{ $t('esgPlatform.assessmentFarmer.acres') || 'acres' }}</div>
              </div>
            </div>

            <div v-if="deforestationField && deforestationField.rawValue && deforestationField.rawValue.deforestationStatus" class="dmd-status-card">
              <div class="dmd-status-heading mb-3">{{ $t('esgPlatform.assessmentFarmer.overallDeforestationProbability') || 'Overall Deforestation Probability' }}</div>
              <div class="dmd-status-pill mb-3 mx-auto" :class="getStatusClass(deforestationField.rawValue.deforestationStatus)">
                {{ deforestationField.rawValue.deforestationStatus }}
              </div>
              <div class="text-caption" style="color: #616161;">
                {{ $t('esgPlatform.assessmentFarmer.reportDate') || 'Report Date' }}:
                <span class="font-weight-bold" style="color: #212121;">{{ formatDateShort(deforestationField.rawValue.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="px-6 pb-5 pt-0">
        <v-spacer />
        <v-btn outlined class="dmd-detail-btn" @click="close">{{ $t('esgPlatform.close') || 'Close' }}</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Geofence map -->
    <v-dialog v-model="geofenceDialog" max-width="980" scrollable>
      <v-card class="dmd-card">
        <div class="d-flex justify-space-between align-start px-6 pt-6 pb-4 dmd-header">
          <div class="dmd-title">{{ geofenceDetails.title || $t('esgPlatform.assessmentFarmer.farmGeofencing') || 'Farm Geofencing' }}</div>
          <v-btn icon @click="geofenceDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <v-card-text class="px-6 pt-4 pb-2">
          <div ref="geofenceMap" class="dmd-map"></div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Location map -->
    <v-dialog v-model="locationDialog" max-width="980" scrollable>
      <v-card class="dmd-card">
        <div class="d-flex justify-space-between align-start px-6 pt-6 pb-4 dmd-header">
          <div class="dmd-title">{{ locationDetails.title || $t('esgPlatform.assessmentFarmer.location') || 'Location' }}</div>
          <v-btn icon @click="locationDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <v-card-text class="px-6 pt-4 pb-2">
          <div ref="locationMap" class="dmd-map"></div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import moment from "moment";
import { getGoogleMapsLoader } from "@/mixins/GoogleMapLoaderSingleton";

export default {
  name: "DimitraModuleDetailDialog",
  props: {
    value: { type: Boolean, default: false },
    payload: { type: Object, default: null },
  },
  data() {
    return {
      geofenceDialog: false,
      geofenceDetails: { title: "", coordinates: [] },
      geofenceMap: null,
      geofencePolygon: null,
      locationDialog: false,
      locationDetails: { title: "", coordinate: null },
      locationMap: null,
      locationMarker: null,
    };
  },
  computed: {
    fields() {
      return Array.isArray(this.payload && this.payload.fields) ? this.payload.fields : [];
    },
    areaField() {
      return this.findField(["area", "total farm size"]);
    },
    areaValue() {
      const f = this.areaField;
      if (!f || f.rawValue === 0 || f.rawValue === null || f.rawValue === undefined) return "-";
      const n = Number(f.rawValue);
      return Number.isFinite(n) ? n.toFixed(1) : String(f.rawValue);
    },
    deforestationField() {
      return this.findField(["deforestation status"]);
    },
    mainFields() {
      const isFarms = this.payload && this.payload.moduleName === "Farms";
      const excluded = new Set(
        [isFarms ? this.areaField : null, this.deforestationField].filter(Boolean).map((f) => f.key)
      );
      excluded.add("id");
      return this.fields.filter((f) => !excluded.has(f.key));
    },
  },
  watch: {
    geofenceDialog(isOpen) {
      if (isOpen) this.$nextTick(() => this.renderGeofenceMap());
      else this.teardownGeofenceMap();
    },
    locationDialog(isOpen) {
      if (isOpen) this.$nextTick(() => this.renderLocationMap());
      else this.teardownLocationMap();
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    findField(labels = []) {
      const norm = labels.map((l) => String(l).trim().toLowerCase());
      return this.fields.find((f) => norm.includes(String(f.key || f.label || "").trim().toLowerCase())) || null;
    },
    isFarmModule(moduleName) {
      return String(moduleName || "").trim().toLowerCase() === "farms";
    },
    getDisplayModuleName(key) {
      return key === "Soil" ? "Soil Management" : key || "Module";
    },
    getTranslatedFieldLabel(field) {
      const key = field && field.key;
      if (key) {
        const t = this.$t(`esgPlatform.dimitraModuleCatalog.fields.${key}`);
        if (t !== `esgPlatform.dimitraModuleCatalog.fields.${key}`) return t;
      }
      return (field && (field.label || field.key)) || "-";
    },
    getLocationCoordinate(location) {
      if (!location || typeof location !== "object") return null;
      const lat = parseFloat(location.lat != null ? location.lat : location.latitude);
      const lng = parseFloat(location.log != null ? location.log : (location.lng != null ? location.lng : location.longitude));
      return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
    },
    getGeofenceCoordinates(row = {}) {
      const raw = Array.isArray(row && row.farmGeofence) ? row.farmGeofence : [];
      return raw
        .map((c) => {
          const lat = parseFloat(c.lat != null ? c.lat : c.latitude);
          const lng = parseFloat(c.log != null ? c.log : (c.lng != null ? c.lng : c.longitude));
          return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
        })
        .filter(Boolean);
    },
    openLocation(title, location) {
      const coordinate = this.getLocationCoordinate(location);
      if (!coordinate) return;
      this.locationDetails = { title: title || "", coordinate };
      this.locationDialog = true;
    },
    openGeofence(title, farmGeofence) {
      const coordinates = this.getGeofenceCoordinates({ farmGeofence });
      if (coordinates.length < 3) return;
      this.geofenceDetails = { title: title || "", coordinates };
      this.geofenceDialog = true;
    },
    async renderGeofenceMap() {
      try {
        const el = this.$refs.geofenceMap;
        const coords = this.geofenceDetails.coordinates || [];
        if (!el || coords.length < 3) return;
        const google = await getGoogleMapsLoader().load();
        this.geofenceMap = new google.maps.Map(el, {
          mapTypeId: "satellite", center: coords[0], zoom: 15, disableDefaultUI: true, zoomControl: true,
        });
        if (this.geofencePolygon) this.geofencePolygon.setMap(null);
        this.geofencePolygon = new google.maps.Polygon({
          paths: coords, strokeColor: "#FF6B00", strokeOpacity: 0.85, strokeWeight: 2,
          fillColor: "#FF6B00", fillOpacity: 0.2, clickable: false, editable: false,
        });
        this.geofencePolygon.setMap(this.geofenceMap);
        const bounds = new google.maps.LatLngBounds();
        coords.forEach((c) => bounds.extend(c));
        this.geofenceMap.fitBounds(bounds, 60);
      } catch (e) { /* fail silently */ }
    },
    teardownGeofenceMap() {
      try { if (this.geofencePolygon) this.geofencePolygon.setMap(null); } catch (e) { /* ignore */ }
      this.geofencePolygon = null;
      this.geofenceMap = null;
    },
    async renderLocationMap() {
      try {
        const el = this.$refs.locationMap;
        const coordinate = this.locationDetails.coordinate;
        if (!el || !coordinate) return;
        const google = await getGoogleMapsLoader().load();
        this.locationMap = new google.maps.Map(el, {
          mapTypeId: "satellite", center: coordinate, zoom: 17, disableDefaultUI: true, zoomControl: true,
        });
        if (this.locationMarker) this.locationMarker.setMap(null);
        this.locationMarker = new google.maps.Marker({ position: coordinate, map: this.locationMap });
      } catch (e) { /* ignore */ }
    },
    teardownLocationMap() {
      try { if (this.locationMarker) this.locationMarker.setMap(null); } catch (e) { /* ignore */ }
      this.locationMarker = null;
      this.locationMap = null;
    },
    formatDateShort(d) {
      return d ? moment(d).format("DD-MM-YYYY") : "-";
    },
    getStatusClass(status) {
      const map = {
        "Very High Deforestation Probability": "v-high-prob",
        "High Deforestation Probability": "high-prob",
        "Medium Deforestation Probability": "medium-prob",
        "Low Deforestation Probability": "low-prob",
        "Very Low Deforestation Probability": "v-low-prob",
        "Zero/Negligible Deforestation Probability": "zero-prob",
        "Manually Mitigated": "manual-prob",
      };
      return map[status] || "";
    },
  },
};
</script>

<style scoped>
.dmd-card { border-radius: 16px; }
.dmd-header { border-bottom: 1px solid #edf1ee; }
.dmd-overline { color: #00bd73; font-size: 12px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
.dmd-title { color: #212121; font-size: 22px; font-weight: 700; line-height: 1.3; }
.dmd-layout { display: grid; grid-template-columns: minmax(0, 2.1fr) minmax(260px, 380px); gap: 28px; }
.dmd-main { min-width: 0; }
.dmd-sidebar { border-left: 1px solid #e6ece8; padding-left: 28px; }
.dmd-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 22px 32px; }
.dmd-grid-item { min-width: 0; }
.dmd-label { color: #7d7d7d; font-size: 13px; font-weight: 500; margin-bottom: 6px; }
.dmd-value { color: #151515; font-size: 15px; font-weight: 600; line-height: 1.5; word-break: break-word; }
.dmd-metric-card, .dmd-status-card {
  background: #ffffff; border: 1px solid #eef2ef; border-radius: 20px;
  box-shadow: 0 8px 24px rgba(35, 45, 38, 0.08); padding: 22px;
}
.dmd-metric-label { color: #7d7d7d; font-size: 16px; font-weight: 700; }
.dmd-metric-value { color: #111111; font-size: 30px; font-weight: 700; line-height: 1.15; }
.dmd-metric-unit { color: #7d7d7d; font-size: 14px; padding-bottom: 4px; }
.dmd-status-card { text-align: center; }
.dmd-status-heading { color: #212121; font-size: 14px; font-weight: 600; }
.dmd-status-pill {
  display: flex; align-items: center; justify-content: center; min-height: 28px;
  max-width: fit-content; padding: 4px 16px; border-radius: 999px; font-size: 14px; font-weight: 600;
}
.v-high-prob { border: 1px solid #8b0000; background: #ff8f8f; color: #8b0000; }
.high-prob { border: 1px solid #8b0000; background: #ffb6b6; color: #8b0000; }
.medium-prob { border: 1px solid #f06b20; background: #ffceb3; color: #f06b20; }
.low-prob { border: 1px solid #f06b20; background: #ffe8c5; color: #f06b20; }
.v-low-prob { border: 1px solid #f49238; background: #fffcec; color: #f49238; }
.zero-prob { border: 1px solid #00bd73; background: #e2fff4; color: #00bd73; }
.manual-prob { border: 1px solid #00bd73; background: #e2fff4; color: #00bd73; }
.dmd-link-btn {
  padding: 0; border: none; background: transparent; cursor: pointer;
  color: #00bd73; font-size: 12px; font-weight: 600;
}
.dmd-link-btn:hover { text-decoration: underline; }
.dmd-detail-btn {
  border-color: #00bd73 !important; color: #00bd73 !important; border-radius: 8px;
  font-size: 12px; font-weight: 700; letter-spacing: 0.04em; min-width: 120px;
}
.dmd-map { width: 100%; height: 70vh; border-radius: 12px; overflow: hidden; border: 1px solid #edf1ee; }
@media (max-width: 960px) {
  .dmd-layout { grid-template-columns: minmax(0, 1fr); }
  .dmd-sidebar { border-left: none; border-top: 1px solid #e6ece8; padding-left: 0; padding-top: 24px; }
  .dmd-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
